import { useEffect, useState } from 'react';

import { useCurrentUser, useEvent } from '@bizzabo/entities';

import client from 'client';
import { useSocket } from '../../../contexts/Socket';

type MeetingsNotificationsResponse = {
    content: Record<string, string>[];
    page: {
        number: number;
        size: number;
        totalPage: number;
        totalElements: number;
    };
};
const fetchMeetingsNotifications = async (eventId: number) => {
    const response = await client().get<MeetingsNotificationsResponse>(
        `/api/v2/meetings/events/${eventId}/notifications?read=false&types=MEETING_CHANGED&types=UPCOMING_MEETING&page=0&size=0`
    );

    return response.data;
};

const useMeetingsNotifications = ({
    eventUniqueName,
    notificationsOnMount = true,
}: {
    eventUniqueName: string;
    notificationsOnMount?: boolean;
}) => {
    const { socket, isConnected } = useSocket();
    const { data: event, isLoading } = useEvent(eventUniqueName);
    const { data: currentUser } = useCurrentUser(event?.id, { enabled: isLoading });
    const [hasNewNotification, setHasNewNotification] = useState<boolean>(false);

    useEffect(() => {
        const initNotifications = async () => {
            const { page } = await fetchMeetingsNotifications(event.id);

            if (page.totalElements > 0) {
                setHasNewNotification(true);
            }
        };

        if (notificationsOnMount && event?.id) {
            initNotifications();
        }
    }, [notificationsOnMount, event?.id]);

    useEffect(() => {
        let subscriber = () => {};

        const subscribe = async () => {
            subscriber = await socket.subscribe({
                domain: 'meetings-service',
                event: 'manage_meetings',
                entityId: currentUser.id,
                cb: async () => {
                    setHasNewNotification(true);
                },
            });
        };

        if (currentUser && isConnected) {
            subscribe();
        }

        return () => {
            subscriber();
        };
    }, [socket, isConnected, currentUser]);

    const resetNotification = () => {
        setHasNewNotification(false);
    };

    return {
        hasNewNotification,
        resetNotification,
        shouldResetNotificationOnSelect: true,
    };
};

export default useMeetingsNotifications;
